<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import CardLabels from 'dashboard/components-next/Conversation/ConversationCard/CardLabels.vue';

const props = defineProps({
  conversation: {
    type: Object,
    required: true,
  },
  accountLabels: {
    type: Array,
    required: true,
  },
});

const { t } = useI18n();

const { getPlainText } = useMessageFormatter();

const lastNonActivityMessageContent = computed(() => {
  const { lastNonActivityMessage = {}, customAttributes = {} } =
    props.conversation;
  const { email: { subject } = {} } = customAttributes;
  return getPlainText(
    subject || lastNonActivityMessage?.content || t('CHAT_LIST.NO_CONTENT')
  );
});

const assignee = computed(() => {
  const { meta: { assignee: agent = {} } = {} } = props.conversation;
  return {
    name: agent.name ?? agent.availableName,
    thumbnail: agent.thumbnail,
    status: agent.availabilityStatus,
  };
});

const unreadMessagesCount = computed(() => {
  const { unreadCount } = props.conversation;
  return unreadCount;
});

const lastMessageOrigin = computed(() => {
  return props.conversation.lastNonActivityMessage?.origin || '';
});

const originLabel = computed(() => {
  const labels = {
    patient: t('CLINICAL_CRM.ORIGINS.patient'),
    linked_phone: t('CLINICAL_CRM.ORIGINS.linked_phone'),
    operator: t('CLINICAL_CRM.ORIGINS.operator'),
    luci: t('CLINICAL_CRM.ORIGINS.luci'),
    automation: t('CLINICAL_CRM.ORIGINS.automation'),
  };
  return labels[lastMessageOrigin.value] || '';
});
</script>

<template>
  <div class="flex flex-col w-full gap-1">
    <div class="flex items-center justify-between w-full gap-2 py-1 h-7">
      <p class="mb-0 text-sm leading-7 text-n-slate-12 line-clamp-1">
        {{ lastNonActivityMessageContent }}
      </p>

      <div
        v-if="unreadMessagesCount > 0"
        class="inline-flex items-center justify-center flex-shrink-0 rounded-full size-5 bg-n-brand"
      >
        <span class="text-xs font-semibold text-white">
          {{ unreadMessagesCount }}
        </span>
      </div>
    </div>

    <div class="grid items-center gap-2.5 h-7 grid-cols-[1fr_20px]">
      <div class="overflow-hidden">
        <div class="flex items-center gap-1.5 overflow-hidden">
          <span
            v-if="originLabel"
            class="flex-shrink-0 px-1.5 py-0.5 rounded-full bg-n-alpha-2 text-n-slate-11 text-xxs font-medium"
          >
            {{ originLabel }}
          </span>
          <CardLabels
            class="min-w-0"
            :conversation-labels="conversation.labels"
            :account-labels="accountLabels"
          />
        </div>
      </div>
      <Avatar
        v-if="assignee.name"
        :name="assignee.name"
        :src="assignee.thumbnail"
        :size="20"
        :status="assignee.status"
        rounded-full
      />
    </div>
  </div>
</template>
